import { Link, useNavigate, useParams } from "react-router-dom";
import { AssignmentTable } from "../components/AssignmentTable";
import { Icon } from "../components/icons";
import { toast } from "../components/toast";
import {
  useMemberAssignments, useMembers, usePatchAssignment,
} from "../lib/proofers";

// A member is `active` the moment they first sign in (claim-on-login binds their
// account) — surfaced as "Live" so the roster reads as a state of the person, not
// of the invite.
const STATUS_LABEL: Record<string, string> = {
  invited: "Invited",
  active: "Live",
  disabled: "Disabled",
};

/** The proofers — open one to see their log (their My Work, as they see it).
 *  Membership + roles + invites live in Settings → Team; this is the read side:
 *  who proofs, and the work each of them holds. Only members with the proofer
 *  role appear. */
export function Proofers() {
  const { data: roster, isLoading } = useMembers();
  const proofers = (roster?.members ?? []).filter((m) => m.roles.includes("proofer") && m.status !== "disabled");
  const nav = useNavigate();

  return (
    <div className="pl-page">
      <header className="pl-head">
        <h1>Proofers</h1>
        <p className="muted">
          Everyone who proofs, and their logs. Open a proofer to see their work — exactly what
          they see. Invite people and set roles in <Link to="/settings/team">Settings → Team</Link>.
        </p>
      </header>

      {isLoading ? (
        <p className="muted">Loading…</p>
      ) : proofers.length === 0 ? (
        <div className="pl-empty">
          No proofers yet — invite one in <Link to="/settings/team">Settings → Team</Link>.
        </div>
      ) : (
        <div className="pl-roster">
          {proofers.map((m) => (
            <div key={m.id} className="pl-rrow">
              <button className="pl-ropen" onClick={() => nav(`/proofers/${m.id}`)} title="Open this proofer's log">
                <span className="pl-rav">{(m.name || m.email).slice(0, 1).toUpperCase()}</span>
                <span className="pl-rmain">
                  <span className="pl-rname">{m.name || m.email}{m.is_self && <span className="team-youtag">You</span>}</span>
                  <span className="pl-remail">{m.email}</span>
                </span>
              </button>
              <span className={`pl-badge s-${m.status}`}>{STATUS_LABEL[m.status] ?? m.status}</span>
              <button className="pl-rchev" onClick={() => nav(`/proofers/${m.id}`)} title="Open">
                <Icon name="chevron-right" size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** One proofer's log — the admin's read-only mirror of that proofer's My Work,
 * plus the Confirm-delivered (accept) action. */
export function ProoferLog() {
  const { memberId } = useParams();
  const nav = useNavigate();
  const { data, isLoading } = useMemberAssignments(memberId);
  const patch = usePatchAssignment();
  const accept = (id: string) =>
    patch.mutate(
      { id, patch: { status: "delivered" } },
      { onSuccess: () => toast("Marked delivered", "success"), onError: () => toast("Couldn't update", "error") },
    );
  const items = data?.assignments ?? [];

  return (
    <div className="pl-page wide">
      <button className="pl-back" onClick={() => nav("/proofers")}>
        <Icon name="chevron-left" size={16} /> Proofers
      </button>
      <header className="pl-head">
        <h1>{data?.proofer_name ?? "Proofer"}</h1>
        <p className="muted">This proofer's log — exactly what they see in My Work.</p>
      </header>
      {isLoading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <div className="pl-empty">No assignments yet. Assign this proofer work from the Workflows board.</div>
      ) : (
        <AssignmentTable
          items={items}
          action={(a) =>
            a.status === "submitted" ? (
              <button className="pl-btn sm ok" onClick={() => accept(a.id)}>
                <Icon name="check" size={13} /> Confirm
              </button>
            ) : null
          } />
      )}
    </div>
  );
}
